import jack from 'jack-logs';
import uuid from 'uuid';
import { hashHistory } from 'react-router';
import { doRequest } from '../requests/actions';
import { signUpClearState } from '../signUp/actions';
import { add as addNotification } from '../messages/notifications/actions';

const updateEvent = new CustomEvent('updateAvailable');

export const actions = {
  SET: 'DEVICE_SET',
  SET_PLATFORM: 'DEVICE_SET_PLATFORM',
  SET_DEVICE_ID: 'DEVICE_SET_DEVICE_ID',
  SET_NICKNAME: 'DEVICE_SET_NICKNAME',
  SET_LOCATION: 'DEVICE_SET_LOCATION',
  REGISTER: 'DEVICE_REGISTER',
  UPDATE_TIME_DELTA: 'DEVICE_UPDATE_TIME_DELTA',
  DECREMENT_DEVELOPER: 'DEVICE_DECREMENT_DEVELOPER',
  CLEAR: 'DEVICE_CLEAR',
  SET_UPDATE_HASH: 'DEVICE_SET_UPDATE_HASH',
};

export function set(device) {
  return {
    type: actions.SET,
    value: device,
  };
}

export function setNickname(nickname) {
  return {
    type: actions.SET_NICKNAME,
    value: nickname,
  };
}

export function setLocation(location) {
  return {
    type: actions.SET_LOCATION,
    value: location,
  };
}

export function setDeviceId(id) {
  return {
    type: actions.SET_DEVICE_ID,
    value: id,
  };
}

export function setPlatform(platform) {
  return {
    type: actions.SET_PLATFORM,
    value: platform,
  };
}

export function clear() {
  return {
    type: actions.CLEAR,
  };
}

export function clearSelf() {
  return (dispatch) => {
    dispatch(clear());
    dispatch(signUpClearState());
    hashHistory.push('/setup');
  };
}
/**
 * Create a new device on the server
 * @param deviceInfo {Object} The device's info
 * @param deviceInfo.location_id {String|int} The location ID this time clock is in.
 * @param [deviceInfo.nickname] {String} A nickname for this device
 * @param [deviceInfo.data_provider] {String} The service provider
 *  for the device (probably Optus or Telstra)
 * @param [deviceInfo.mobile_number] {String} The device's mobile number
 * @param [deviceInfo.sim_number] {String} The device's sim number
 * @param [deviceInfo.serial_number] {String} The device's serial number
 * @param [deviceInfo.imei] {String} The device's IMEI
 * @param [deviceInfo.model] {String} The device's model
 * @returns {Promise} (Undefined) Thenable once the timeclock is created
 */
export function createDevice(deviceInfo) {
  return async (dispatch) => {
    // redact any empty fields from device info
    const sendInfo = Object.keys(deviceInfo).reduce((previous, key) => {
      const info = previous;
      if (deviceInfo[key] !== '' && deviceInfo[key] != null) {
        info[key] = deviceInfo[key].toString().trim();
      }
      return info;
    }, {});

    const device = await dispatch(doRequest({
      endpoint: '/devices',
      method: 'POST',
      data: sendInfo,
    }));

    if (!device) {
      throw new Error('Failed to create device');
    }

    dispatch(setDeviceId(device.id));
    return device;
  };
}

export function register(nickname, location) {
  return async (dispatch) => {
    // clear any existing device
    dispatch(clear());
    dispatch(setNickname(nickname));
    dispatch(setLocation(location));
    if (!window.cordova) {
      dispatch(setPlatform('browser'));
    }

    const deviceInfo = {
      nickname,
      location_id: location.id,
      os: 'browser',
    };

    if (window) {
      if (window.device) {
        dispatch(setPlatform(window.device.platform));
      }
      if (window.getDeviceData instanceof Function) {
        const specs = await window.getDeviceData();
        Object.assign(deviceInfo, {
          ...specs,
          serial_number: specs.serial || null,
        });

        jack.dev.log('Registering device', deviceInfo);
      }
    }

    return dispatch(createDevice(deviceInfo)).catch((e) => {
      dispatch(addNotification({ type: 'error', body: 'Enter a nickname or select a location' }));
      throw e;
    });
  };
}

export function updateTimeDelta(delta) {
  return {
    type: actions.UPDATE_TIME_DELTA,
    value: delta,
  };
}

export function checkTimeDelta() {
  return async (dispatch) => {
    const url = 'https://timeclock.tanda.co/time';
    const requestId = uuid().replace(/-/g, '');
    jack.dev.log({ message: 'Request starting', request: { url, request_id: requestId, method: 'GET' }, createdAt: Date.now() });

    const res = await fetch(url);
    const data = {
      response: {
        status: res.status,
        type: res.headers.get('content-type'),
      },
    };

    if (res.ok) {
      const json = await res.json();
      const delta = Date.now() - json.time;
      data.newDelta = delta;
      if (Math.abs(delta) > 1000) { // disregard deltas less than +-1 second
        dispatch(updateTimeDelta(delta));
      } else {
        dispatch(updateTimeDelta(0));
      }
    }

    jack.log({
      message: 'Request complete',
      request_id: requestId,
      ...data,
      createdAt: Date.now(),
    });
  };
}

export function decrementDeveloper() {
  return {
    type: actions.DECREMENT_DEVELOPER,
  };
}

export function setUpdateHash(hash) {
  return {
    type: actions.SET_UPDATE_HASH,
    value: hash,
  };
}

export function checkForUpdates() {
  return async (dispatch, getState) => {
    if (__DEV__) {
      return null;
    }

    const currentVersion = process.env.BUNDLE_VERSION;
    const branchSlug = process.env.BRANCH_SLUG;
    const mostRecentDownloadedVersion = getState().getIn(['device', 'hash']);

    const res = await fetch(`https://timeclock.tanda.co/version/${branchSlug}`);
    if (!res.ok) {
      return null;
    }
    const version = (await res.text()).trim();
    if (version !== currentVersion && version !== mostRecentDownloadedVersion) {
      // we've downloaded a new version
      dispatch(setUpdateHash(version));
      document.dispatchEvent(updateEvent);
    }
    return null;
  };
}

/**
 * Thin wrapper around the checkTimeDelta function to match other reducers
 * @returns {function(*): *}
 */
export function refresh() {
  return (dispatch) => {
    dispatch(checkTimeDelta());
    dispatch(checkForUpdates());
  };
}



// WEBPACK FOOTER //
// ./src/store/device/actions.js